import { useCallback } from "react";
import Papa from "papaparse";
import { ConfigAppSDK } from "@contentful/app-sdk";
import { useSDK } from "@contentful/react-apps-toolkit";
import { GlossaryLocalePair } from "../lib";
import { useFetchDeeplGlossaryLocales } from "../services";

enum GLOSSARY_COLUMNS {
    SOURCE_ENTRY,
    TARGET_ENTRY,
    SOURCE_LANG,
    TARGET_LANG,
}

const getGlossaryLocales = (file: File): Promise<GlossaryLocalePair[]> => {
    return new Promise((resolve, reject) => {
        const glossaryLocales: GlossaryLocalePair[] = [];

        Papa.parse(file, {
            step: function (results) {
                const data = results.data as string[];
                const sourceLang = data[GLOSSARY_COLUMNS.SOURCE_LANG];
                const targetLang = data[GLOSSARY_COLUMNS.TARGET_LANG];

                if (
                    !glossaryLocales.find(
                        pair => pair.source === sourceLang && pair.target === targetLang
                    )
                ) {
                    glossaryLocales.push({ source: sourceLang, target: targetLang });
                }
            },
            error: function (error) {
                reject(error);
            },
            complete: function () {
                if (glossaryLocales.length === 0) {
                    reject(new Error("Empty glossary"));
                }
                resolve(glossaryLocales);
            },
            skipEmptyLines: "greedy",
            delimiter: ",",
            quoteChar: '"',
        });
    });
};

const localesUpperCase = (locales: GlossaryLocalePair[]): GlossaryLocalePair[] =>
    locales.map(pair => ({ source: pair.source.toUpperCase(), target: pair.target.toUpperCase() }));

const checkLocaleAvailability = (
    glossaryLocales: GlossaryLocalePair[],
    deeplGlossaryLocales: GlossaryLocalePair[]
) => {
    const goodGlossaryLocales: GlossaryLocalePair[] = [];
    const badGlossaryLocales: GlossaryLocalePair[] = [];
    glossaryLocales.forEach(pair => {
        if (
            deeplGlossaryLocales.find(
                deeplPair => deeplPair.source === pair.source && deeplPair.target === pair.target
            )
        ) {
            goodGlossaryLocales.push(pair);
        } else {
            badGlossaryLocales.push(pair);
        }
    });

    if (badGlossaryLocales.length === glossaryLocales.length) {
        throw new Error("The languages in your glossary are not supported, sorry.");
    }

    return {
        goodGlossaryLocales,
        badGlossaryLocales,
    };
};

export const useProcessCSV = () => {
    const sdk = useSDK<ConfigAppSDK>();
    const deeplCredentials = sdk.parameters.installation.deeplApiKey;
    const { data: deeplGlossaryLocales } = useFetchDeeplGlossaryLocales({
        skip: !deeplCredentials,
    });

    const processCSV = useCallback(
        async (file: File) => {
            if (!deeplGlossaryLocales) {
                throw new Error("Could not fetch DeepL glossary locales");
            }
            const glossaryLocales = await getGlossaryLocales(file);
            const { goodGlossaryLocales, badGlossaryLocales } = checkLocaleAvailability(
                localesUpperCase(glossaryLocales),
                deeplGlossaryLocales
            );

            return {
                goodGlossaryLocales,
                badGlossaryLocales,
            };
        },
        [deeplGlossaryLocales]
    );

    return { processCSV };
};
